import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { hasAdminRights, isBoard } from '../utils/roleUtils';
import ConfirmationModal from '../components/ConfirmationModal';
import GuestArchiveDialog from '../components/GuestArchiveDialog';
import { GuestArchiveStatus, GuestArchiveView, inArchiveView } from '../utils/guestArchive';
import { ModalShell, ModalActions } from '../components/modals/core';
import GuestDetailModal from '../components/modals/GuestDetailModal';
import MemberAvatar from '../components/MemberAvatar';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';
import {
  UserGroupIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  TrashIcon,
  ClockIcon,
  TicketIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ArchiveBoxIcon,
  EyeIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';


interface GuestCardType {
  id: number;
  name: string;
  total_entries: number;
  price: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface Guest {
  mg_nr: string;
  mitglied_nr: string;
  name: string;
  vorname: string;
  email: string;
  tel_nummer?: string;
  eintritt?: string;
  foto_path?: string | null;
  foto_path_updated?: string | number | null;
  last_login: string | null;
  last_booking: string | null;
  cards: any[];
  archive?: GuestArchiveStatus;
}

interface GuestCard {
  id: number;
  guest_card_type_id: number;
  member_id: string;
  remaining_entries: number;
  total_entries?: number;
  purchase_date: string;
  expiry_date: string;
  is_active?: boolean;
  bezahlt?: boolean;
  bezahlt_am?: string | null;
  type?: GuestCardType;
  card_name?: string;
}

interface Stats {
  totalRevenue: number;
  paidRevenue: number;
  totalSoldCards: number;
  activeGuestCards: number;
  totalBookedTickets: number;
  bookedThisMonth: number;
  bookedLastMonth: number;
  soldThisMonth: number;
  soldLastMonth: number;
  avgCardsPerGuest: string;
  totalEvents: number;
  bookedEvents: number;
  availableYears: number[];
  cardTypes?: any;
  revenueByCardType?: any;
  monthlyPurchasesByType?: any;
  monthlyBookingsByType?: any;
  monthlyTotalPurchases?: any;
  topMonthsByRevenue?: any[];
  allMonths?: string[];
  unpaidBookingMembers?: any[];
}

interface ExtendedStats {
  timeSeries: Array<{
    date: string;
    purchases: { count: number; revenue: number; unique_buyers: number };
    payments: { count: number; revenue: number; avg_delay: number | null };
    bookings: { count: number; unique_users: number; unique_events: number };
    cumulative: {
      purchases: number;
      revenue: number;
      payments: number;
      bookings: number;
    };
  }>;
  monthly: any[];
  cardTypes: any[];
  paymentAnalysis: any[];
  utilization: any[];
  retention: any[];
  year: number;
}

const GuestCardManagement: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const [stats, setStats] = useState<Stats | null>(null);
  const [extendedStats, setExtendedStats] = useState<ExtendedStats | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [filteredGuests, setFilteredGuests] = useState<Guest[]>([]);
  const [cardTypes, setCardTypes] = useState<GuestCardType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyWithCards, setShowOnlyWithCards] = useState(false);
  const [archiveView, setArchiveView] = useState<GuestArchiveView>('active');
  const [inactiveMonths, setInactiveMonths] = useState(12);
  const [selectedGuestIds, setSelectedGuestIds] = useState<string[]>([]);
  const [archiveDialog, setArchiveDialog] = useState<{ guests: Guest[]; archived: boolean } | null>(null);
  const [guestLoadError, setGuestLoadError] = useState('');
  const guestRequestId = useRef(0);
  const [archiveFeedback, setArchiveFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateCardModal, setShowCreateCardModal] = useState(false);
  const [guestCards, setGuestCards] = useState<GuestCard[]>([]);
  const [sortField, setSortField] = useState<'name' | 'last_login' | 'last_booking'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const createCardFormRef = useRef<HTMLFormElement>(null);

  const guestImageKey = (guest: Guest) => `${guest.mg_nr}:${guest.foto_path || ''}:${guest.foto_path_updated || ''}`;

  // Profilbild-URL eines Gasts (nur wenn foto_path vorhanden), analog zu VerzehrkostenBeta.
  const getGuestImageUrl = (guest: Guest): string | undefined => {
    if (!guest.foto_path) return undefined;
    const token = localStorage.getItem('jwt_token');
    return `${BASE_URL}/api/members/user-image-url/${guest.mg_nr}?size=S&token=${token}&ts=${guest.foto_path_updated || ''}`;
  };

  const [newCardType, setNewCardType] = useState({
    name: '',
    total_entries: '',
    price: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    unlimited: true,
  });


  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  const hasAccess = hasAdminRights(user);
  const canManageArchive = isBoard(user);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/stats');
      if (response.data.success && response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (error: any) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch extended statistics for charts
  const fetchExtendedStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/stats/extended');
      if (response.data.success && response.data.data) {
        setExtendedStats(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching extended stats:', error);
    }
  }, []);

  // Fetch guests with cards
  const fetchGuests = useCallback(async () => {
    const requestId = ++guestRequestId.current;
    try {
      setGuestLoadError('');
      const response = await apiClient.get(`/api/guest-cards/guests-with-cards?archiveOverview=1&months=${inactiveMonths}`);
      if (requestId !== guestRequestId.current) return;
      if (!response.data.success) throw new Error('Gästeliste nicht verfügbar');
      setGuests(response.data.guests);
    } catch (error) {
      if (requestId === guestRequestId.current) setGuestLoadError('Gäste konnten nicht geladen werden. Bitte erneut versuchen.');
    }
  }, [inactiveMonths]);

  // Fetch card types
  const fetchCardTypes = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/types/all');
      if (response.data) {
        setCardTypes(response.data.cardTypes || []);
      }
    } catch (error) {
      console.error('Error fetching card types:', error);
    }
  }, []);

  // Fetch guest details
  const fetchGuestDetails = useCallback(async (mg_nr: string) => {
    console.log('=== fetchGuestDetails ===');
    console.log('Fetching details for mg_nr:', mg_nr);
    console.log('Current guestCards state:', guestCards);

    try {
      const cardsResponse = await apiClient.get(`/api/guest-cards/guest-cards?member_id=${mg_nr}`);
      console.log('API Response:', cardsResponse.data);

      if (cardsResponse.data.success) {
        const cards = cardsResponse.data.cards || [];
        console.log('Cards from API:', cards);

        // Preserve card_name from current guestCards state if not in response
        const cardsWithNames = cards.map((card: any) => {
          // Try to find the existing card name from current state
          const existingCard = guestCards.find(gc => gc.id === card.id);
          console.log(`Card ${card.id}:`, {
            'from_api_card_name': card.card_name,
            'from_api_name': card.name,  // Log the 'name' field
            'existing_card': existingCard,
            'existing_card_name': existingCard?.card_name,
            'card_type': card.type,
            'card_type_name': card.type?.name
          });

          const finalCardName = card.card_name ||
                              card.name ||  // API gibt "name" statt "card_name" zurück!
                              existingCard?.card_name ||
                              card.type?.name ||
                              'Gästekarte';

          console.log(`Final card_name for card ${card.id}:`, finalCardName);

          return {
            ...card,
            card_name: finalCardName
          };
        });

        console.log('Cards with names:', cardsWithNames);
        setGuestCards(cardsWithNames);

        // Update selectedGuest with the detailed card data including bookings
        setSelectedGuest(prev => ({
          ...prev!,
          cards: cardsWithNames
        }));
        
        // Extract all bookings from all cards (with full data including trainer images, colors, etc.)
        const allBookings = cards.flatMap((card: any) => card.bookings || []);
      } else {
        // Handle non-success response
        setGuestCards([]);
      }
    } catch (error: any) {
      console.error('Error fetching guest details:', error);
      // Set empty data on error to show modal with no cards
      setGuestCards([]);
      
      // Only show error message for non-401 errors (401 is handled by apiClient interceptor)
      if (error.response?.status !== 401) {
        // You could add a notification here if needed
        console.error('Failed to load guest details. Server returned:', error.response?.status);
      }
    }
  }, [guestCards]);

  // Apply filters and sorting
  const applyFiltersAndSort = useCallback(() => {
    let result = guests.filter(guest => inArchiveView(guest.archive, archiveView));
    
    // Filter by cards
    if (showOnlyWithCards) {
      result = result.filter(g => Array.isArray(g.cards) && g.cards.length > 0);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        g =>
          (g.name && g.name.toLowerCase().includes(term)) ||
          (g.vorname && g.vorname.toLowerCase().includes(term)) ||
          (g.email && g.email.toLowerCase().includes(term))
      );
    }
    
    // Sort
    result.sort((a, b) => {
      let valueA: any = a[sortField];
      let valueB: any = b[sortField];
      
      if (sortField === 'name') {
        valueA = `${a.vorname} ${a.name}`.toLowerCase();
        valueB = `${b.vorname} ${b.name}`.toLowerCase();
      } else if (sortField === 'last_login' || sortField === 'last_booking') {
        valueA = valueA ? new Date(valueA).getTime() : 0;
        valueB = valueB ? new Date(valueB).getTime() : 0;
      }
      
      if (valueA === valueB) return 0;
      if (valueA === 0) return 1;
      if (valueB === 0) return -1;
      
      return sortOrder === 'asc' 
        ? (valueA < valueB ? -1 : 1) 
        : (valueA > valueB ? -1 : 1);
    });
    
    setFilteredGuests(result);
  }, [guests, searchTerm, sortField, sortOrder, showOnlyWithCards, archiveView]);

  useEffect(() => {
    if (hasAccess) {
      setLoading(true);
      Promise.all([
        fetchStats(),
        fetchGuests(),
        fetchCardTypes(),
        fetchExtendedStats()
      ])
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [hasAccess, fetchStats, fetchGuests, fetchCardTypes, fetchExtendedStats]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  const selectableGuests = filteredGuests.filter(guest => archiveView === 'archived' || guest.archive?.can_archive);
  useEffect(() => {
    const visible = new Set(filteredGuests.filter(guest => archiveView === 'archived' || guest.archive?.can_archive).map(guest => String(guest.mg_nr)));
    setSelectedGuestIds(previous => previous.filter(id => visible.has(id)));
  }, [filteredGuests, archiveView]);
  const toggleGuestSelection = (id: string) => setSelectedGuestIds(previous => previous.includes(id) ? previous.filter(item => item !== id) : [...previous, id]);
  const saveArchive = async (reason: string, confirmed: boolean) => {
    if (!archiveDialog) return;
    await apiClient.post('/api/guest-cards/archive', {
      member_ids: archiveDialog.guests.map(guest => guest.mg_nr),
      archived: archiveDialog.archived, reason, confirm_remaining: confirmed,
    });
    setSelectedGuestIds([]);
    setArchiveFeedback(`${archiveDialog.guests.length} ${archiveDialog.guests.length === 1 ? 'Gast' : 'Gäste'} ${archiveDialog.archived ? 'archiviert' : 'reaktiviert'}.`);
    // Speichern und Neuladen getrennt behandeln: ein Ladefehler ist kein Speicherfehler.
    await fetchGuests().catch(() => {});
  };

  const handleSort = (field: 'name' | 'last_login' | 'last_booking') => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Check if cancellation is allowed (24h after event)

  const handleViewDetails = (guest: Guest) => {
    console.log('=== handleViewDetails ===');
    console.log('Guest data:', guest);
    console.log('Guest cards:', guest.cards);

    setSelectedGuest(guest);

    // Use existing cards data if available as initial data
    if (guest.cards && Array.isArray(guest.cards)) {
      // Set initial data from guest object
      const mappedCards = guest.cards.map((card: any) => {
        console.log('Mapping card:', card);
        console.log('card.card_name:', card.card_name);
        console.log('card.type:', card.type);

        const mappedCard = {
          ...card,
          member_id: guest.mg_nr,
          // Ensure required fields have defaults
          remaining_entries: card.remaining_entries || 0,
          total_entries: card.total_entries || card.type?.total_entries || 0,
          purchase_date: card.purchase_date || card.created_at || '',
          expiry_date: card.expiry_date || '',
          is_active: card.is_active !== undefined ? card.is_active : true,
          bezahlt: card.bezahlt || false,
          bezahlt_am: card.bezahlt_am || null,
          card_name: card.card_name || card.type?.name || 'Gästekarte',
        };

        console.log('Mapped card result:', mappedCard);
        return mappedCard;
      });

      console.log('All mapped cards:', mappedCards);
      setGuestCards(mappedCards);
    } else {
      setGuestCards([]);
    }
    
    // Show modal immediately
    setShowDetailsModal(true);

    // Fetch details after a small delay to ensure state is set
    setTimeout(() => {
      fetchGuestDetails(guest.mg_nr);
    }, 100);
  };


  const handleCreateCardType = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const cardData = {
        ...newCardType,
        end_date: newCardType.unlimited ? null : newCardType.end_date,
        is_active: true  // Stelle sicher, dass neue Karten aktiv sind
      };
      await apiClient.post('/api/guest-cards/types', cardData);
      setShowCreateCardModal(false);
      setNewCardType({
        name: '',
        total_entries: '',
        price: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        unlimited: true,
      });
      fetchCardTypes();
    } catch (error) {
      console.error('Error creating card type:', error);
    }
  };

  const handleDeactivateCardType = (cardType: GuestCardType) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Kartentyp deaktivieren',
      message: `Möchten Sie den Kartentyp "${cardType.name}" wirklich deaktivieren?`,
      variant: 'two',
      onConfirm: () => handleDeactivateConfirm(cardType.id)
    });
  };

  const handleDeactivateConfirm = async (cardTypeId: number) => {
    try {
      await apiClient.put(`/api/guest-cards/types/deactivate/${cardTypeId}`);
      fetchCardTypes();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Kartentyp wurde erfolgreich deaktiviert.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Error deactivating card type:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Fehler beim Deaktivieren des Kartentyps.',
        variant: 'single'
      });
    }
  };

  const getInactivityStatus = (guest: Guest) => {
    const lastActivity = guest.archive?.last_activity || [guest.last_login, guest.last_booking].filter(Boolean).sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime())[0];
    if (!lastActivity) {
      return { status: 'never', color: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400', label: 'Nie aktiv' };
    }
    
    const lastActivityDate = new Date(lastActivity);
    const now = new Date();
    const daysInactive = Math.floor((now.getTime() - lastActivityDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysInactive < 0) return { status: 'upcoming', color: 'bg-info-100 text-info-700 dark:bg-info-900/20 dark:text-info-400', label: `Termin am ${lastActivityDate.toLocaleDateString('de-DE')}` };
    if (daysInactive <= 30) {
      return { status: 'active', color: 'bg-success-100 dark:bg-success-900/20 text-success-700 dark:text-success-400', label: `Vor ${daysInactive} ${daysInactive === 1 ? 'Tag' : 'Tagen'}` };
    } else if (daysInactive <= 90) {
      return { status: 'returning', color: 'bg-warning-100 dark:bg-warning-900/20 text-warning-700 dark:text-warning-400', label: `Vor ${daysInactive} Tagen` };
    } else {
      return { status: 'inactive', color: 'bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-400', label: `Vor ${Math.floor(daysInactive / 30)} Monaten` };
    }
  };

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
    
    // Remove ,00 for whole numbers
    return formatted.replace(',00', '');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '...';

    // Handle invalid dates
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '...';
    }

    return date.toLocaleDateString('de-DE');
  };

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationCircleIcon className="h-12 w-12 text-error-500 mx-auto mb-4" />
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Sie haben keine Berechtigung, diese Seite zu sehen.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col">
      {/* Header */}
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <TicketIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Gästekartenverwaltung</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Desktop Search Field */}
            <div className="hidden md:block relative w-[280px]">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Gast suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-11 pl-11 pr-10 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full"
                >
                  <XMarkIcon className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Search */}
        <div className="sm:hidden mb-3">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Gast suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-10 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>

      {/* Hauptkennzahlen: direkter Überblick vor der Arbeitsliste */}
      {stats && (
        <div className="order-1 mt-4 sm:mt-6">
          <StatGroup columns={3}>
            <StatCard
              icon={BanknotesIcon}
              tone="success"
              valueTone="success"
              label="Gesamteinnahmen"
              value={formatCurrency(stats.totalRevenue)}
            />
            <StatCard
              icon={TicketIcon}
              tone="primary"
              label="Verkaufte Karten"
              value={stats.totalSoldCards}
            />
            <StatCard
              icon={CheckCircleIcon}
              tone="info"
              label="Gebuchte Tickets"
              value={stats.totalBookedTickets}
            />
          </StatGroup>
        </div>
      )}

      {/* Weiterführende Statistiken */}
      {stats && (
        <div className="order-2 mt-4 sm:mt-6">
          {/* Card Type Statistics */}
          {stats.cardTypes && Object.keys(stats.cardTypes).length > 0 && (
            <div
              className="mb-4 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:mb-6 sm:p-6 sm:shadow-md"
            >
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white sm:mb-4 sm:text-lg">Kartentyp-Statistik</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 sm:gap-4">
                {Object.entries(stats.cardTypes).map(([typeId, cardType]: [string, any]) => (
                  <div
                    key={typeId}
                    className="rounded-xl bg-gray-50 p-3 dark:bg-gray-700/50 sm:p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                          <TicketIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{cardType.name}</h4>
                      </div>
                      <span className="text-sm font-medium text-success-600 dark:text-success-400">
                        {formatCurrency(cardType.price)}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <AttributeLabel iconKey="count" className="text-gray-500 dark:text-gray-400" textClassName="text-sm">Verkauft:</AttributeLabel>
                        <span className="font-medium text-gray-900 dark:text-white">{cardType.total_count}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <AttributeLabel iconKey="payment" className="text-gray-500 dark:text-gray-400" textClassName="text-sm">Bezahlt:</AttributeLabel>
                        <span className="font-medium text-success-600 dark:text-success-400">{cardType.paid_count}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <AttributeLabel iconKey="booking" className="text-gray-500 dark:text-gray-400" textClassName="text-sm">Gebucht:</AttributeLabel>
                        <span className="font-medium text-primary-600 dark:text-primary-400">{cardType.booked_count}/{cardType.total_count * cardType.total_entries}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <AttributeLabel iconKey="income" className="text-gray-500 dark:text-gray-400" textClassName="text-sm">Einnahmen:</AttributeLabel>
                          <span className="font-bold text-success-600 dark:text-success-400">
                            {formatCurrency(cardType.total_count * cardType.price)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unpaid Bookings Warning */}
          {stats.unpaidBookingMembers && stats.unpaidBookingMembers.length > 0 && (
            <div className="mb-4 rounded-2xl border border-warning-200 bg-warning-50 p-3 shadow-sm dark:border-warning-800 dark:bg-warning-900/20 sm:mb-6 sm:p-4 sm:shadow-md">
              <div className="flex items-start gap-3">
                <ExclamationCircleIcon className="h-6 w-6 text-warning-600 dark:text-warning-400 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-warning-800 dark:text-warning-200 mb-2">
                    Unbezahlte Karten mit Buchungen
                  </h3>
                  <div className="space-y-1">
                    {stats.unpaidBookingMembers.map((member: any) => (
                      <div key={member.member_id} className="flex items-center justify-between text-sm">
                        <span className="text-warning-700 dark:text-warning-300">
                          {member.vorname} {member.name} - {member.card_types}
                        </span>
                        <span className="text-warning-600 dark:text-warning-400 font-medium">
                          {member.booking_count} Buchung{member.booking_count !== 1 ? 'en' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Monthly Trends Charts */}
          {stats.allMonths && stats.allMonths.length > 0 && (
            <div className="mb-4 grid grid-cols-1 gap-3 sm:mb-6 sm:gap-6 lg:grid-cols-2">
              {/* Monthly Sales Chart */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 shadow-md border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Monatliche Verkäufe</h3>
                  <ChartBarIcon className="h-4 w-4 text-gray-400" />
                </div>
                <div className="space-y-2">
                  {stats.allMonths.slice(0, 6).map((month: string) => {
                    const monthData = stats.monthlyTotalPurchases?.[month] || 0;
                    const maxValue = Math.max(...Object.values(stats.monthlyTotalPurchases || {0: 1}) as number[]);
                    const percentage = (monthData / maxValue) * 100;
                    const [year, monthNum] = month.split('-');
                    const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
                    
                    return (
                      <div key={month} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400 w-14">{monthName}</span>
                        <div className="flex-1 bg-white dark:bg-gray-700/50 rounded-full h-5 relative">
                          <div 
                            className="absolute left-0 top-0 h-full bg-info-500/20 dark:bg-info-400/20 rounded-full transition-all duration-300"
                            style={{ width: `${Math.max(percentage, 5)}%` }}
                          />
                          <span className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-xs text-gray-600 dark:text-gray-400 z-10">
                            {monthData}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Monthly Bookings Chart */}
              {extendedStats?.monthly && extendedStats.monthly.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 shadow-md border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Monatliche Buchungen</h3>
                    <ChartBarIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="space-y-2">
                    {(() => {
                      // Group by month and sum total_bookings
                      const monthlyData: { [key: string]: number } = {};
                      extendedStats.monthly.forEach((item: any) => {
                        if (!monthlyData[item.month]) {
                          monthlyData[item.month] = 0;
                        }
                        monthlyData[item.month] += item.total_bookings || 0;
                      });

                      // Get all unique months sorted
                      const allMonths = Object.keys(monthlyData).sort().reverse();
                      const maxValue = Math.max(...Object.values(monthlyData)) || 1;

                      return allMonths.slice(0, 6).map((month: string) => {
                        const totalBookings = monthlyData[month];
                        const percentage = (totalBookings / maxValue) * 100;
                        const [year, monthNum] = month.split('-');
                        const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });

                        return (
                          <div key={month} className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 w-14">{monthName}</span>
                            <div className="flex-1 bg-white dark:bg-gray-700/50 rounded-full h-5 relative">
                              <div
                                className="absolute left-0 top-0 h-full bg-primary-500/20 dark:bg-primary-400/20 rounded-full transition-all duration-300"
                                style={{ width: `${Math.max(percentage, 5)}%` }}
                              />
                              <span className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-xs text-gray-600 dark:text-gray-400 z-10">
                                {totalBookings}
                              </span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Top Revenue Months */}
          {stats.topMonthsByRevenue && stats.topMonthsByRevenue.length > 0 && (
            <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:mb-6 sm:p-6 sm:shadow-md">
              <div className="mb-2 flex items-center justify-between sm:mb-4">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">Top Umsatz-Monate</h3>
                <ArrowTrendingUpIcon className="h-[18px] w-[18px] text-success-500 sm:h-5 sm:w-5" />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4">
                {stats.topMonthsByRevenue.slice(0, 3).map((item: any, index: number) => {
                  const [year, monthNum] = item.month.split('-');
                  const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
                  const medalColors = ['text-warning-500', 'text-gray-400', 'text-warning-600'];

                  return (
                    <div key={item.month} className="flex items-center gap-2.5 rounded-lg bg-gray-50 px-3 py-2.5 dark:bg-gray-700/50 sm:gap-3 sm:p-3">
                      <div className={`w-6 flex-shrink-0 text-base font-bold sm:w-auto sm:text-2xl ${medalColors[index]}`}>
                        {index + 1}.
                      </div>
                      <div className="flex min-w-0 flex-1 items-center justify-between gap-2 sm:block">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{monthName}</p>
                        <p className="whitespace-nowrap text-sm font-bold text-success-600 dark:text-success-400 sm:text-lg">
                          {formatCurrency(item.revenue)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Extended Stats */}
          {extendedStats && (
            <div className="space-y-6 mb-6">
              {/* Payment Delay and Customer Retention - Side by Side */}
              {(extendedStats.paymentAnalysis?.length > 0 || extendedStats.retention?.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Payment Delay Analysis */}
                  {extendedStats.paymentAnalysis && extendedStats.paymentAnalysis.length > 0 && (
                    <div className="bg-white dark:bg-[#1c2432] rounded-xl p-4 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Zahlungsverzögerungen</h3>
                        <ClockIcon className="h-4 w-4 text-warning-500" />
                      </div>
                      <div className="space-y-2">
                        {extendedStats.paymentAnalysis.slice(0, 5).map((item: any) => (
                          <div key={item.delay_category} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                                {item.count}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                                {item.delay_category}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm font-medium text-success-600 dark:text-success-400 whitespace-nowrap">
                              {item.total_revenue > 999 ? `${(item.total_revenue/1000).toFixed(1)}k€` : formatCurrency(item.total_revenue)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Customer Retention */}
                  {extendedStats.retention && extendedStats.retention.length > 0 && (
                    <div className="bg-white dark:bg-[#1c2432] rounded-xl p-4 shadow-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Kundenbindung</h3>
                        <UserGroupIcon className="h-4 w-4 text-primary-500" />
                      </div>
                      <div className="space-y-2">
                        {extendedStats.retention.map((item: any) => (
                          <div key={item.customer_type} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="text-base sm:text-lg font-bold text-primary-600 dark:text-primary-400">
                                {item.count}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-900 dark:text-white truncate">
                                {item.customer_type}
                              </div>
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              Ø {parseFloat(item.avg_active_months || '0').toFixed(1)} Mon.
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Card Types Management */}
      <div className="card order-3 mt-4 sm:mt-6">
        <div className="flex flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
              <CreditCardIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Gästekarten
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {cardTypes.length} Kartentyp{cardTypes.length !== 1 ? 'en' : ''} verfügbar
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateCardModal(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-md text-sm flex-shrink-0"
          >
            <PlusIcon className="h-5 w-5" />
            Gästekarte
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cardTypes.length > 0 ? (
            cardTypes.map((cardType) => {
              const isExpired = cardType.end_date && cardType.end_date !== '9999-12-31' && new Date(cardType.end_date) < new Date();
              const isUpcoming = new Date(cardType.start_date) > new Date();
              
              return (
                <div
                  key={cardType.id}
                  className={`relative rounded-xl shadow-md border transition-all duration-200 hover:shadow-md ${
                    isExpired
                      ? 'bg-error-50/50 dark:bg-error-900/10 border-error-200 dark:border-error-800'
                      : isUpcoming
                      ? 'bg-info-50/50 dark:bg-info-900/10 border-info-200 dark:border-info-800'
                      : 'bg-white dark:bg-[#1c2432] border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {/* Status Badge */}
                  {(isExpired || isUpcoming) && (
                    <div className="absolute -top-2 -right-2 z-10">
                      {isExpired && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-error-500 text-white rounded-full shadow-md">
                          Abgelaufen
                        </span>
                      )}
                      {isUpcoming && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-info-500 text-white rounded-full shadow-md">
                          Zukünftig
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                          <TicketIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {cardType.name}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            ID: #{cardType.id}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between py-1.5 px-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <AttributeLabel iconKey="count" tone="primary" textClassName="text-sm text-gray-600 dark:text-gray-400">Trainings:</AttributeLabel>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {cardType.total_entries}
                        </span>
                      </div>

                      <div className="flex items-center justify-between py-1.5 px-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <AttributeLabel iconKey="amount" textClassName="text-sm text-gray-600 dark:text-gray-400">Preis:</AttributeLabel>
                        <span className="text-sm font-bold text-success-600 dark:text-success-400">
                          {formatCurrency(cardType.price)}
                        </span>
                      </div>

                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <AttributeLabel iconKey="date" size="sm" className="text-xs text-gray-500 dark:text-gray-400" textClassName="text-xs">Gültig:</AttributeLabel>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {formatDate(cardType.start_date)} - {formatDate(cardType.end_date)}
                          </p>
                          {!isExpired && (
                            <button
                              onClick={() => handleDeactivateCardType(cardType)}
                              className="p-1 text-error-600 hover:text-error-700 dark:text-error-400 dark:hover:text-error-300 hover:bg-error-50 dark:hover:bg-error-900/20 rounded transition-colors"
                              title="Löschen"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full text-center py-8">
              <CreditCardIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Keine Kartentypen vorhanden</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Erstellen Sie einen neuen Kartentyp</p>
            </div>
          )}
        </div>
      </div>

      {/* Guests List */}
      <div
        className="order-1 mt-4 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-[#1c2432] sm:mt-6 sm:p-6 sm:shadow-md"
      >
        <div className="mb-5 space-y-4">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Gästeansicht">
            {([
              ['active', 'Aktive Gäste'], ['archived', 'Archiv'], ['suggestions', 'Archivvorschläge'],
            ] as Array<[GuestArchiveView, string]>).map(([view, label]) => <button key={view} type="button"
              aria-pressed={archiveView === view}
              onClick={() => { setArchiveView(view); setSelectedGuestIds([]); setArchiveFeedback(''); }}
              className={`min-h-[44px] rounded-xl border px-4 text-sm font-semibold ${archiveView === view ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'}`}>
              {label} ({guests.filter(guest => inArchiveView(guest.archive, view)).length})
            </button>)}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Das Archiv blendet Gäste aus dieser Übersicht aus. Karten, Guthaben und die Statistiken über alle Gäste bleiben erhalten.</p>
          {archiveView === 'suggestions' && <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <label className="flex flex-wrap items-center gap-3 text-sm font-medium text-gray-700 dark:text-gray-200">Ohne Training, neue Buchung oder Kartenkauf seit
              <select aria-label="Inaktivitätszeitraum" value={inactiveMonths} onChange={event => { setInactiveMonths(Number(event.target.value)); setSelectedGuestIds([]); }} className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 dark:border-gray-600 dark:bg-gray-900 dark:text-white">
                {[6, 12, 24].map(months => <option key={months} value={months}>{months} Monaten</option>)}
              </select>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">Bei Gästen ohne Aktivität zählt die Registrierung. Offene Zahlungen und zukünftige Termine sperren die Archivierung. Resttickets bitte manuell prüfen.</p>
          </div>}
          {guestLoadError && <div role="alert" className="text-sm text-error-600 dark:text-error-400">{guestLoadError} <button type="button" onClick={() => fetchGuests().catch(() => {})} className="min-h-[44px] underline">Erneut laden</button></div>}
          {archiveFeedback && <p role="status" className="text-sm text-success-700 dark:text-success-400">{archiveFeedback}</p>}
          {canManageArchive ? <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-200">
            <label className="flex min-h-[44px] cursor-pointer items-center gap-2">
              <input type="checkbox" aria-label="Alle angezeigten archivierbaren Gäste auswählen" className="h-5 w-5" disabled={!selectableGuests.length || !!guestLoadError}
                checked={!!selectableGuests.length && selectableGuests.every(guest => selectedGuestIds.includes(String(guest.mg_nr)))}
                onChange={event => setSelectedGuestIds(event.target.checked ? selectableGuests.map(guest => String(guest.mg_nr)) : [])} />
              Alle verfügbaren auswählen · {selectedGuestIds.length} ausgewählt
            </label>
            <button type="button" disabled={!selectedGuestIds.length || !!guestLoadError}
              onClick={() => setArchiveDialog({ guests: filteredGuests.filter(guest => selectedGuestIds.includes(String(guest.mg_nr))), archived: archiveView !== 'archived' })}
              className="min-h-[44px] rounded-xl bg-primary-600 px-4 font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
              {archiveView === 'archived' ? 'Auswahl reaktivieren' : 'Auswahl archivieren'}
            </button>
          </div> : <p className="text-sm text-gray-500 dark:text-gray-400">Archivieren und Reaktivieren ist dem Vorstand vorbehalten.</p>}
        </div>
        <div className="mb-3 sm:mb-4">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
              Gäste ({filteredGuests.length})
            </h2>

            {/* Filter Chips */}
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:gap-3">
              <button
                onClick={() => setShowOnlyWithCards(false)}
                className={`min-h-9 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-all sm:px-4 sm:py-2 ${
                  !showOnlyWithCards
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'bg-white dark:bg-[#1c2432] border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white hover:border-primary-500'
                }`}
              >
                Alle Gäste
              </button>
              <button
                onClick={() => setShowOnlyWithCards(true)}
                className={`min-h-9 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-all sm:px-4 sm:py-2 ${
                  showOnlyWithCards
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'bg-white dark:bg-[#1c2432] border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white hover:border-primary-500'
                }`}
              >
                Mit Karten
              </button>
            </div>
          </div>
          

        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full table-fixed text-sm">
            <colgroup className="hidden md:table-column-group">
              <col className="w-[30%]" /><col className="w-[16%]" /><col className="w-[19%]" /><col className="w-[18%]" /><col className="w-[17%]" />
            </colgroup>
            <thead className="hidden bg-gray-50 text-xs text-gray-500 dark:bg-gray-800/60 dark:text-gray-400 md:table-header-group">
              <tr>
                <th className="px-4 py-3 text-left font-medium"><button onClick={() => handleSort('name')} title="Nach Name sortieren" className="inline-flex min-h-[40px] items-center gap-2">Gast <span aria-hidden="true">{sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}</span></button></th>
                <th className="px-3 py-3 text-left font-medium">Karten</th>
                <th className="px-3 py-3 text-left font-medium">Tickets</th>
                <th className="px-4 py-3 text-left font-medium">Letzte Aktivität</th>
                <th className="px-4 py-3 text-right font-medium">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {!filteredGuests.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">{guestLoadError ? 'Gästeliste derzeit nicht verfügbar.' : archiveView === 'archived' ? 'Keine archivierten Gäste für diese Filter.' : archiveView === 'suggestions' ? 'Keine Archivvorschläge für diese Filter und diesen Zeitraum.' : 'Keine aktiven Gäste für diese Filter.'}</td></tr>}
              {filteredGuests.map(guest => {
                const cards = Array.isArray(guest.cards) ? guest.cards : [];
                const total = cards.reduce((sum, card) => sum + Number(card.total_entries || 0), 0);
                const remaining = cards.reduce((sum, card) => sum + Number(card.remaining_entries || 0), 0);
                const paid = cards.filter(card => !!card.bezahlt_am).length;
                const unpaid = cards.length - paid;
                const lastActivity = guest.archive?.last_activity || guest.last_booking;
                const archived = !!guest.archive?.archived_at;
                const blocked = !archived && !guest.archive?.can_archive;
                return <tr key={guest.mg_nr} className="grid grid-cols-2 gap-x-3 gap-y-3 bg-white p-4 transition-colors hover:bg-gray-50/70 dark:bg-gray-800/20 dark:hover:bg-gray-800/60 md:table-row md:p-0">
                  <td className="col-span-2 min-w-0 md:px-4 md:py-5 md:align-middle">
                    <div className="flex items-center gap-3">
                      {canManageArchive && <input type="checkbox" aria-label={`${guest.vorname} ${guest.name} auswählen`}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 accent-primary-600 disabled:opacity-40 dark:border-gray-600"
                        disabled={!!guestLoadError || blocked} checked={selectedGuestIds.includes(String(guest.mg_nr))}
                        onChange={() => toggleGuestSelection(String(guest.mg_nr))} />}
                      <div className="shrink-0"><MemberAvatar member={guest} role="guest" size="md" photoUrl={getGuestImageUrl(guest)}
                        hasImageError={imageErrors[guestImageKey(guest)]} onImageError={() => setImageErrors(previous => ({ ...previous, [guestImageKey(guest)]: true }))} /></div>
                      <div className="min-w-0">
                        <p className="break-words font-semibold leading-5 text-gray-900 dark:text-white">{guest.vorname} {guest.name}</p>
                        <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{archived ? `Archiviert am ${formatDate(guest.archive!.archived_at!)}` : `Gast · Nr. ${guest.mg_nr}`}</p>
                        {archived && guest.archive?.reason && <p className="mt-1 break-words text-xs text-gray-500 dark:text-gray-400">{guest.archive.reason}</p>}
                        {blocked && <p className="mt-1 text-xs text-warning-700 dark:text-warning-400">{guest.archive?.blockers.join(' · ')}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="min-w-0 md:px-3 md:py-5 md:align-middle">
                    <p className="font-semibold leading-5 text-gray-900 dark:text-gray-100">{cards.length} {cards.length === 1 ? 'Karte' : 'Karten'}</p>
                    {cards.length > 0 && <p className="mt-1 text-xs leading-5 text-success-700 dark:text-success-400">{paid} bezahlt</p>}
                    {unpaid > 0 && <p className="mt-1 text-xs font-medium text-warning-700 dark:text-warning-400">{unpaid} unbezahlt</p>}
                  </td>
                  <td className="min-w-0 text-right md:px-3 md:py-5 md:text-left md:align-middle">
                    <p className="font-semibold leading-5 text-gray-900 dark:text-gray-100">{remaining} {remaining === 1 ? 'Restticket' : 'Resttickets'}</p>
                    <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{Math.max(0, total - remaining)} von {total} genutzt</p>
                    {archiveView === 'suggestions' && remaining > 0 && <span title="Resttickets vor der Archivierung prüfen" className="mt-1 inline-flex rounded-md bg-warning-50 px-2 py-0.5 text-[11px] font-medium text-warning-800 dark:bg-warning-900/20 dark:text-warning-300">Resttickets prüfen</span>}
                  </td>
                  <td className="col-span-2 flex items-center justify-between border-t border-gray-100 pt-3 text-right md:table-cell md:border-0 md:px-3 md:py-5 md:text-left md:align-middle dark:border-gray-700">
                    <span className="text-xs text-gray-500 md:hidden dark:text-gray-400">Letzte Aktivität</span>
                    <div><p className="font-medium leading-5 text-gray-700 dark:text-gray-200">{lastActivity ? formatDate(lastActivity) : 'Unbekannt'}</p>
                    <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{lastActivity ? getInactivityStatus(guest).label : 'Manuell prüfen'}</p></div>
                  </td>
                  <td className="col-span-2 border-t border-gray-100 pt-3 md:border-0 md:px-4 md:py-5 md:align-middle dark:border-gray-700">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => handleViewDetails(guest)} aria-label={`Details zu ${guest.vorname} ${guest.name}`} title="Details anzeigen"
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-primary-300"><EyeIcon className="h-5 w-5" aria-hidden="true" /></button>
                      {canManageArchive && <button type="button" aria-label={`${guest.vorname} ${guest.name} ${archived ? 'reaktivieren' : 'archivieren'}`}
                        disabled={!!guestLoadError || blocked} title={blocked ? guest.archive?.blockers.join(' · ') : archived ? 'Reaktivieren' : 'Archivieren'}
                        onClick={() => setArchiveDialog({ guests: [guest], archived: !archived })}
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-primary-300">
                        {archived ? <ArrowUturnLeftIcon className="h-5 w-5" aria-hidden="true" /> : <ArchiveBoxIcon className="h-5 w-5" aria-hidden="true" />}
                      </button>}
                    </div>
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>

      {archiveDialog && <GuestArchiveDialog guests={archiveDialog.guests} archived={archiveDialog.archived} onClose={() => setArchiveDialog(null)} onSave={saveArchive} />}
      {/* Gastdetails — dieselbe Komponente nutzt das Vorstands-Dashboard */}
      <GuestDetailModal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        guest={selectedGuest}
        onChanged={() => {
          if (selectedGuest) fetchGuestDetails(selectedGuest.mg_nr);
          fetchGuests();
        }}
      />

      {/* Create Card Type Modal */}
      <ModalShell
        open={showCreateCardModal}
        onClose={() => setShowCreateCardModal(false)}
        variant="form"
        size="sm"
        title="Neuer Kartentyp"
        icon={CreditCardIcon}
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: () => setShowCreateCardModal(false) }}
            primary={{ label: 'Erstellen', onClick: () => createCardFormRef.current?.requestSubmit() }}
          />
        }
      >
        <form ref={createCardFormRef} onSubmit={handleCreateCardType} className="space-y-4">
          <div>
            <AttributeLabel iconKey="name" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Name
            </AttributeLabel>
            <input
              type="text"
              required
              value={newCardType.name}
              onChange={(e) => setNewCardType({ ...newCardType, name: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500"
              placeholder="z.B. 10er Karte"
            />
          </div>

          <div>
            <AttributeLabel iconKey="count" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Anzahl Trainings
            </AttributeLabel>
            <input
              type="number"
              required
              min="1"
              value={newCardType.total_entries}
              onChange={(e) => setNewCardType({ ...newCardType, total_entries: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <AttributeLabel iconKey="amount" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Preis (€)
            </AttributeLabel>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={newCardType.price}
              onChange={(e) => setNewCardType({ ...newCardType, price: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500"
              placeholder="z.B. 50.00"
            />
          </div>

          <div>
            <AttributeLabel iconKey="date" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Startdatum
            </AttributeLabel>
            <input
              type="date"
              required
              value={newCardType.start_date}
              onChange={(e) => setNewCardType({ ...newCardType, start_date: e.target.value })}
              className="w-full bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="flex items-center mb-3">
              <input
                type="checkbox"
                checked={newCardType.unlimited}
                onChange={(e) => setNewCardType({
                  ...newCardType,
                  unlimited: e.target.checked,
                  end_date: e.target.checked ? '' : newCardType.end_date
                })}
                className="mr-2 rounded text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Unbeschränkt gültig (kein Enddatum)
              </span>
            </label>

            {!newCardType.unlimited && (
              <div>
                <AttributeLabel iconKey="date" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Enddatum
                </AttributeLabel>
                <input
                  type="date"
                  required={!newCardType.unlimited}
                  value={newCardType.end_date}
                  onChange={(e) => setNewCardType({ ...newCardType, end_date: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500"
                  min={newCardType.start_date}
                />
              </div>
            )}
          </div>
        </form>
      </ModalShell>

      {/* ConfirmationModal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        destructive={confirmModal.variant === 'two' && confirmModal.type === 'warning' && /stornier/i.test(confirmModal.title || '')}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant || 'single'}
        showCloseButton={true}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              if (confirmModal.onConfirm) {
                confirmModal.onConfirm();
              }
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          }
        } : undefined}
      />
    </div>
  );
};

export default GuestCardManagement;
